import { Injectable, NgZone, inject } from "@angular/core";
import { DashSortableService } from "../../../../modules/home/dash/service/dash.sortable.service";
import { setDashPanelHeaderDragPreview } from "../../../../modules/home/dash/service/dash.sortable.preview.util";
import {
    DASH_SORTABLE_DRAG_PREVIEW_CLASS,
    DASH_SORTABLE_DRAG_THRESHOLD_PX,
    DASH_SORTABLE_HANDLE_SELECTOR,
    DASH_SORTABLE_LIST_ATTR,
    DASH_SORTABLE_PANEL_SELECTOR,
    DASH_SORTABLE_PLACEHOLDER_CLASS,
} from "../../../../modules/home/dash/service/dash.sortable.constants";
import type { HomeDashSortableDropPayload, HomeDashSortableListRegistration } from "./home.dash.sortable.model";

interface PendingPointerDrag {
    readonly listRegistration: HomeDashSortableListRegistration;
    readonly panelElement: HTMLElement;
    readonly pointerId: number;
    readonly startClientX: number;
    readonly startClientY: number;
}

interface ActiveDragSession {
    readonly listRegistration: HomeDashSortableListRegistration;
    readonly panelElement: HTMLElement;
    readonly fromIndex: number;
    readonly previewElement: HTMLElement;
    readonly placeholderElement: HTMLElement;
    readonly pointerOffsetX: number;
    readonly pointerOffsetY: number;
    targetListRegistration: HomeDashSortableListRegistration;
    targetIndex: number;
}

@Injectable({ providedIn: "root" })
export class HomeDashSortableDragService {
    private readonly ngZone = inject(NgZone);
    private readonly dashSortableService = inject(DashSortableService);

    private readonly listRegistrations = new Map<HTMLElement, HomeDashSortableListRegistration>();

    private pendingPointerDrag: PendingPointerDrag | undefined;
    private activeDragSession: ActiveDragSession | undefined;

    private readonly boundPointerMove = (event: PointerEvent): void => this.handleDocumentPointerMove(event);
    private readonly boundPointerUp = (event: PointerEvent): void => this.handleDocumentPointerUp(event);

    registerList(registration: HomeDashSortableListRegistration): () => void {
        registration.hostElement.setAttribute(DASH_SORTABLE_LIST_ATTR, "true");
        this.listRegistrations.set(registration.hostElement, registration);

        const onPointerDown = (event: PointerEvent): void => {
            this.ngZone.runOutsideAngular(() => this.handleListPointerDown(event, registration));
        };
        registration.hostElement.addEventListener("pointerdown", onPointerDown);

        return () => {
            registration.hostElement.removeEventListener("pointerdown", onPointerDown);
            registration.hostElement.removeAttribute(DASH_SORTABLE_LIST_ATTR);
            this.listRegistrations.delete(registration.hostElement);
        };
    }

    private handleListPointerDown(event: PointerEvent, listRegistration: HomeDashSortableListRegistration): void {
        if (event.button !== 0 || listRegistration.getIsDisabled() || this.activeDragSession) {
            return;
        }

        const handleElement =
            event.target instanceof Element ? event.target.closest(DASH_SORTABLE_HANDLE_SELECTOR) : null;
        if (!(handleElement instanceof HTMLElement)) {
            return;
        }

        const panelElement = handleElement.closest(DASH_SORTABLE_PANEL_SELECTOR);
        if (!(panelElement instanceof HTMLElement) || !listRegistration.hostElement.contains(panelElement)) {
            return;
        }

        this.pendingPointerDrag = {
            listRegistration,
            panelElement,
            pointerId: event.pointerId,
            startClientX: event.clientX,
            startClientY: event.clientY,
        };
        document.addEventListener("pointermove", this.boundPointerMove);
        document.addEventListener("pointerup", this.boundPointerUp);
        document.addEventListener("pointercancel", this.boundPointerUp);
    }

    private handleDocumentPointerMove(event: PointerEvent): void {
        if (this.pendingPointerDrag && event.pointerId !== this.pendingPointerDrag.pointerId) {
            return;
        }

        if (!this.activeDragSession && this.pendingPointerDrag) {
            const deltaX = event.clientX - this.pendingPointerDrag.startClientX;
            const deltaY = event.clientY - this.pendingPointerDrag.startClientY;
            if (Math.hypot(deltaX, deltaY) < DASH_SORTABLE_DRAG_THRESHOLD_PX) {
                return;
            }
            this.beginDrag(this.pendingPointerDrag, event);
            this.pendingPointerDrag = undefined;
        }

        if (!this.activeDragSession) {
            return;
        }

        this.movePreview(event.clientX, event.clientY);
        this.updatePlaceholderPosition(event.clientX, event.clientY);
    }

    private handleDocumentPointerUp(event: PointerEvent): void {
        if (this.pendingPointerDrag && event.pointerId !== this.pendingPointerDrag.pointerId) {
            return;
        }

        this.removeDocumentPointerListeners();

        if (this.activeDragSession) {
            this.finishDrag();
        }

        this.pendingPointerDrag = undefined;
    }

    private beginDrag(pending: PendingPointerDrag, event: PointerEvent): void {
        const { listRegistration, panelElement } = pending;
        const fromIndex = this.resolvePanelIndex(listRegistration, panelElement);
        if (fromIndex < 0) {
            return;
        }

        const panelRect = panelElement.getBoundingClientRect();
        const previewElement = document.createElement("div");
        previewElement.className = DASH_SORTABLE_DRAG_PREVIEW_CLASS;
        previewElement.style.position = "fixed";
        previewElement.style.pointerEvents = "none";
        previewElement.style.zIndex = "10000";
        previewElement.style.opacity = "0.8";
        previewElement.style.left = `${panelRect.left}px`;
        previewElement.style.top = `${panelRect.top}px`;
        document.body.appendChild(previewElement);

        const placeholderElement = document.createElement("div");
        placeholderElement.className = `slot ${DASH_SORTABLE_PLACEHOLDER_CLASS}`;
        placeholderElement.setAttribute("aria-hidden", "true");
        listRegistration.hostElement.insertBefore(placeholderElement, panelElement);

        panelElement.style.display = "none";
        document.body.style.userSelect = "none";

        this.dashSortableService.onDragStarted(panelElement, listRegistration.getIsToolbox());
        requestAnimationFrame(() => {
            setDashPanelHeaderDragPreview(previewElement, panelElement, listRegistration.getIsToolbox());
        });

        this.activeDragSession = {
            listRegistration,
            panelElement,
            fromIndex,
            previewElement,
            placeholderElement,
            pointerOffsetX: event.clientX - panelRect.left,
            pointerOffsetY: event.clientY - panelRect.top,
            targetListRegistration: listRegistration,
            targetIndex: fromIndex,
        };

        this.movePreview(event.clientX, event.clientY);
    }

    private movePreview(clientX: number, clientY: number): void {
        const session = this.activeDragSession;
        if (!session) {
            return;
        }
        session.previewElement.style.left = `${clientX - session.pointerOffsetX}px`;
        session.previewElement.style.top = `${clientY - session.pointerOffsetY}px`;
    }

    private updatePlaceholderPosition(clientX: number, clientY: number): void {
        const session = this.activeDragSession;
        if (!session) {
            return;
        }

        const targetListElement = this.findListElementAtPoint(clientX, clientY);
        if (!targetListElement) {
            return;
        }

        const targetRegistration = this.listRegistrations.get(targetListElement);
        if (!targetRegistration || targetRegistration.getIsDisabled()) {
            return;
        }

        const targetIndex = this.resolveInsertIndex(targetListElement, clientY, session.panelElement);
        session.targetListRegistration = targetRegistration;
        session.targetIndex = targetIndex;
        this.movePlaceholder(targetListElement, targetIndex, session.placeholderElement);
    }

    private finishDrag(): void {
        const session = this.activeDragSession;
        if (!session) {
            return;
        }

        const dropPayload: HomeDashSortableDropPayload = {
            sourcePanels: session.listRegistration.getPanels(),
            targetPanels: session.targetListRegistration.getPanels(),
            fromIndex: session.fromIndex,
            toIndex: session.targetIndex,
            sourceIsToolbox: session.listRegistration.getIsToolbox(),
            targetIsToolbox: session.targetListRegistration.getIsToolbox(),
        };

        const positionChanged =
            dropPayload.sourcePanels !== dropPayload.targetPanels || dropPayload.fromIndex !== dropPayload.toIndex;

        if (positionChanged) {
            session.targetListRegistration.applyDrop(dropPayload);
            session.listRegistration.notifyViewChanged();
            if (session.targetListRegistration !== session.listRegistration) {
                session.targetListRegistration.notifyViewChanged();
            }
        }

        this.cleanupDragSession();
        this.dashSortableService.onDragEnded();
    }

    private cleanupDragSession(): void {
        const session = this.activeDragSession;
        if (!session) {
            return;
        }

        session.previewElement.remove();
        session.placeholderElement.remove();
        session.panelElement.style.display = "";
        document.body.style.userSelect = "";
        this.activeDragSession = undefined;
    }

    private movePlaceholder(listElement: HTMLElement, index: number, placeholderElement: HTMLElement): void {
        const panelElements = this.getVisiblePanelElements(listElement);
        if (index >= panelElements.length) {
            listElement.appendChild(placeholderElement);
            return;
        }
        listElement.insertBefore(placeholderElement, panelElements[index]);
    }

    private findListElementAtPoint(clientX: number, clientY: number): HTMLElement | undefined {
        const hitElement = document.elementFromPoint(clientX, clientY);
        const listElement = hitElement?.closest(`[${DASH_SORTABLE_LIST_ATTR}]`);
        return listElement instanceof HTMLElement ? listElement : undefined;
    }

    private resolveInsertIndex(listElement: HTMLElement, clientY: number, draggedElement: HTMLElement): number {
        const panelElements = this.getVisiblePanelElements(listElement, draggedElement);
        if (!panelElements.length) {
            return 0;
        }

        for (let panelOrd = 0; panelOrd < panelElements.length; panelOrd++) {
            const panelRect = panelElements[panelOrd].getBoundingClientRect();
            const midpointY = panelRect.top + panelRect.height / 2;
            if (clientY < midpointY) {
                return panelOrd;
            }
        }

        return panelElements.length;
    }

    private getVisiblePanelElements(listElement: HTMLElement, excludeElement?: HTMLElement): HTMLElement[] {
        return Array.from(listElement.children).filter((child): child is HTMLElement => {
            if (!(child instanceof HTMLElement)) {
                return false;
            }
            if (child === excludeElement) {
                return false;
            }
            if (child.classList.contains(DASH_SORTABLE_PLACEHOLDER_CLASS)) {
                return false;
            }
            return child.matches(DASH_SORTABLE_PANEL_SELECTOR);
        });
    }

    private resolvePanelIndex(listRegistration: HomeDashSortableListRegistration, panelElement: HTMLElement): number {
        const panelIdAttr = panelElement.getAttribute("panel-id");
        const panelId = panelIdAttr != null ? parseInt(panelIdAttr, 10) : NaN;
        if (Number.isNaN(panelId)) {
            return -1;
        }
        return listRegistration.getPanels().findIndex((panel) => panel.panelId === panelId);
    }

    private removeDocumentPointerListeners(): void {
        document.removeEventListener("pointermove", this.boundPointerMove);
        document.removeEventListener("pointerup", this.boundPointerUp);
        document.removeEventListener("pointercancel", this.boundPointerUp);
    }
}
